/**
 * QuestionAttachment — the upload slot under a question that asks for a document.
 *
 * A prompt like "Attach a copy of the pharmacy receipt" rendered as a text box
 * and nothing else: the patient was asked for a document with nowhere to put it
 * (case imp_adc19063, Michael, 2026-09-18). The backend now flags those
 * questions (`attachment: true`, questionnaire_builder.question_wants_attachment)
 * and the questionnaire renders this control under the prompt.
 *
 * Contract:
 *   - Camera on a phone, file picker everywhere (two inputs: iOS Safari drops
 *     the photo-library option when `capture` is set, so one input cannot do
 *     both).
 *   - Images and PDFs only; images are downscaled before sealing, exactly as
 *     the insurer-letter upload does.
 *   - The upload is fire-and-forget and NEVER blocks Continue. A failed send
 *     shows a retry-able message and the patient carries on.
 *   - The bytes go through the sealed-box path to the Mac — the same encrypted
 *     route as an insurer letter. Nothing is put in the answers payload (a
 *     Firestore bridge document dies over ~700 KB).
 *
 * React 17 safe (no React-18-only hooks); inline styles, matching the
 * questionnaire card's palette.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { sealAndUploadQuestionAttachment } from "../../lib/questionAttachmentUpload";

const ACCEPTED_TYPES = "image/*,application/pdf";

const C = {
  ink: "#182a33", muted: "#5b6e7c", border: "#e6dccb", teal: "#1b5e6e",
  tealTint: "#e4ecee", alert: "#c25450", alertTint: "#fff6f5", paper: "#fdf9f5",
  green: "#38534a", greenTint: "#eef4f2", greenBdr: "#bbd5cc",
};

type UploadStatus = "sending" | "sent" | "failed";

interface AttachedFile {
  id: string;
  name: string;
  status: UploadStatus;
  /** Object URL for an image preview; null for PDFs. */
  previewUrl: string | null;
  error?: string;
}

interface BridgeSessionLike {
  sessionToken?: string;
  assignmentId?: string;
  pubkeyB64?: string;
  [key: string]: unknown;
}

interface QuestionAttachmentProps {
  /** The question's answer key — what the document gets filed against. */
  questionTarget: string;
  /** The question's prompt — the caption the physician sees on the row. */
  questionLabel?: string;
  /** Test seam: use this bridge session instead of the stored one. */
  session?: BridgeSessionLike | null;
  children?: React.ReactNode;
}

let _seq = 0;
function nextId(): string {
  _seq += 1;
  return `att_${_seq}_${Date.now()}`;
}

export default function QuestionAttachment({
  questionTarget,
  questionLabel,
  session = null,
}: QuestionAttachmentProps) {
  const [files, setFiles] = useState<AttachedFile[]>([]);
  const cameraRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef<HTMLInputElement>(null);
  // Object URLs outlive React state on unmount, so they are revoked by hand.
  const urlsRef = useRef<string[]>([]);

  useEffect(() => {
    return () => {
      for (const url of urlsRef.current) {
        try { URL.revokeObjectURL(url); } catch { /* already revoked */ }
      }
      urlsRef.current = [];
    };
  }, []);

  const update = useCallback((id: string, patch: Partial<AttachedFile>) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  }, []);

  async function handleFile(file: File) {
    const id = nextId();
    let previewUrl: string | null = null;
    if (file.type && file.type.startsWith("image/")) {
      try {
        previewUrl = URL.createObjectURL(file);
        urlsRef.current.push(previewUrl);
      } catch { previewUrl = null; }
    }
    setFiles((prev) => [...prev, { id, name: file.name, status: "sending", previewUrl }]);

    try {
      await sealAndUploadQuestionAttachment(
        file,
        { questionTarget, questionLabel },
        session,
      );
      update(id, { status: "sent", error: undefined });
    } catch (err) {
      // Never rethrow: a failed attachment must not stop the questionnaire.
      update(id, {
        status: "failed",
        error: (err as Error)?.message || "Couldn't send that file. Please try again.",
      });
    }
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files && e.target.files[0];
    // Reset so the same file can be picked again after a failure.
    e.target.value = "";
    if (file) void handleFile(file);
  }

  function removeFile(id: string) {
    setFiles((prev) => {
      const gone = prev.find((f) => f.id === id);
      if (gone?.previewUrl) {
        try { URL.revokeObjectURL(gone.previewUrl); } catch { /* already revoked */ }
        urlsRef.current = urlsRef.current.filter((u) => u !== gone.previewUrl);
      }
      return prev.filter((f) => f.id !== id);
    });
  }

  const btn: React.CSSProperties = {
    padding: "9px 14px", borderRadius: 10, fontSize: 14, fontWeight: 600,
    border: `2px solid ${C.border}`, background: "#fff", color: C.teal, cursor: "pointer",
  };

  return (
    <div style={{ marginTop: 12, padding: "12px 14px", background: C.paper, border: `1px dashed ${C.border}`, borderRadius: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>
        Attach a photo or file
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>
        This question asks for a document. Photograph it or upload a file (image or PDF) —
        you can still continue if you'd rather send it later.
      </div>

      {/* Two inputs on purpose: `capture` opens the camera on a phone but hides
          the photo library on iOS Safari, so the picker gets its own input. */}
      <input
        ref={cameraRef}
        type="file"
        accept={ACCEPTED_TYPES}
        capture="environment"
        style={{ display: "none" }}
        onChange={onInputChange}
        aria-label={`Photograph a document for: ${questionLabel || questionTarget}`}
      />
      <input
        ref={pickerRef}
        type="file"
        accept={ACCEPTED_TYPES}
        style={{ display: "none" }}
        onChange={onInputChange}
        aria-label={`Choose a file for: ${questionLabel || questionTarget}`}
      />

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
        <button type="button" style={btn} onClick={() => cameraRef.current?.click()}>
          Take a photo
        </button>
        <button type="button" style={btn} onClick={() => pickerRef.current?.click()}>
          Choose a file
        </button>
      </div>

      {files.length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0" }}>
          {files.map((f) => (
            <li
              key={f.id}
              style={{
                display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", marginTop: 8,
                borderRadius: 9, background: f.status === "failed" ? C.alertTint : C.greenTint,
                border: `1px solid ${f.status === "failed" ? C.alert : C.greenBdr}`,
              }}
            >
              {f.previewUrl ? (
                <img
                  src={f.previewUrl}
                  alt=""
                  style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6, flexShrink: 0 }}
                />
              ) : (
                <span aria-hidden="true" style={{ fontSize: 20, flexShrink: 0 }}>📄</span>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {f.name}
                </div>
                <div style={{ fontSize: 12, color: f.status === "failed" ? C.alert : C.green, marginTop: 1 }}>
                  {f.status === "sending" && "Sending…"}
                  {f.status === "sent" && "Sent to your care team"}
                  {f.status === "failed" && (f.error || "Couldn't send that file.")}
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeFile(f.id)}
                aria-label={
                  f.status === "sent"
                    ? `Remove ${f.name} from this list (your care team already has it)`
                    : `Remove ${f.name}`
                }
                title={f.status === "sent" ? "Remove from this list" : "Remove"}
                style={{
                  flexShrink: 0, border: "none", background: "transparent", color: C.muted,
                  fontSize: 18, lineHeight: 1, cursor: "pointer", padding: "2px 6px",
                }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
